<template>
  <div class="app-header">
    <div class="header-content">
      <section class="left-section">
        <div class="card-header govbrand-header" @click="goToRoot()">
          <img
            :src="
              isContrast
                ? require('@/assets/images/APP_HEADER_LOGO_contrast.png')
                : require('@/assets/images/APP_HEADER_LOGO.png')
            "
            class="is-hidden-touch"
            alt=""
          />
          <img
            :src="
              isContrast
                ? require('@/assets/images/SUITE_WORD_BLACK.png')
                : require('@/assets/images/SUITE_WORD_WHITE.png')
            "
            class="is-hidden-desktop"
            alt=""
          />
        </div>
        <b-field class="search-input">
          <b-autocomplete
            expanded
            role="searchbox"
            :value="searchFilter"
            :data="getFields"
            autocomplete
            field="name"
            name="search"
            :placeholder="General.PLACEHOLDER.SEARCH_BY_NUP_NAME_SUBJECT"
            :loading="isFetching"
            icon="magnify"
            keepFirst
            :iconRight="searchFilter ? 'close' : ''"
            iconRightClickable
            @icon-right-click="searchFilter = ''"
            @typing="setSearchField"
            @focus="onFocusSearch"
            @blur="onBlurSearch"
            @select="option => onSelectSuggestion(option)"
          />
        </b-field>
      </section>
      <section class="right-section">
        <template v-if="!searchFocus || !isMobile">
          <b-tag
            v-if="isBeta || isQA"
            class="env-tag spacing-mr-15 has-text-weight-semibold is-hidden-touch"
            size="is-medium"
            :type="isBeta ? `is-white` : `is-yellow`"
            rounded
          >
            {{
              isBeta
                ? General.TEXTS.BETA_TAG.toUpperCase()
                : General.TEXTS.QA_TAG.toUpperCase()
            }}
          </b-tag>

          <MessageHeader
            @click.native="
              isMobile ? openCollapseComponent(MessageHeaderContent) : null
            "
          />
          <NotificationBell
            @click.native="
              isMobile ? openCollapseComponent(NotificationBellContent) : null
            "
          />

          <span
            class="is-hidden-desktop menu-user"
            role="button"
            @click="
              isMobile ? openCollapseComponent(UserHeaderMenuContent) : null
            "
          >
            <img
              :src="
                isContrast
                  ? require('../assets/svg/user-circle-contrast.svg')
                  : require('../assets/svg/user-circle.svg')
              "
              alt="Perfil do usuário"
              width="30"
            />
          </span>

          <b-dropdown
            v-if="user.capacity"
            ref="menu"
            position="is-bottom-left"
            :mobileModal="false"
            aria-role="menu"
            class="is-hidden-touch"
          >
            <button
              slot="trigger"
              slot-scope="{ active }"
              class="button-reset-style profile-info"
              role="button"
            >
              <img
                :src="
                  isContrast
                    ? require('../assets/svg/user-circle-contrast.svg')
                    : require('../assets/svg/user-circle.svg')
                "
                alt="Perfil do usuário"
                width="40"
              />
              <span class="user-profile has-text-left">
                <b>{{ user.name }}</b>
                <div v-if="user.capacity.capacity_abbreviation">
                  {{ formatCapacity(user.capacity) }}
                </div>
                <div v-else>{{ General.WITHOUT_CAPACITY }}</div>
                <div v-if="user.capacity.category_description">
                  {{ user.capacity.category_description }}
                </div>
                <div v-else-if="user.capacity.is_secondary">
                  {{ General.SECONDARY_CAPACITY.toUpperCase() }}
                </div>
              </span>
              <b-icon
                :type="isContrast ? 'is-black' : 'is-white'"
                size="is-small"
                :icon="active ? 'chevron-up' : 'chevron-down'"
              />
            </button>
            <b-dropdown-item
              v-if="user.capacity.capacity_abbreviation"
              value="profile"
              aria-role="menuitem"
              @click="toggleProfilesDropdown"
            >
              <b-icon
                icon="account-circle-outline"
                size="is-medium"
                class="m-5"
              />
              <div class="menu-profile spacing-ml-5">
                <span>{{ formatCapacity(user.capacity) }}</span>
                {{ truncate(user.capacity.category_description) }}
                <div class="is-flex is-justify-content-center mt-5">
                  <b-button
                    :type="isContrast ? 'is-white' : 'is-primary'"
                    size="is-small"
                    class="has-text-weight-semibold"
                    outlined
                  >
                    {{ General.CHANGE_SECTOR.toLowerCase() }}
                  </b-button>
                </div>
              </div>
            </b-dropdown-item>
            <hr
              v-if="user.capacity.capacity_abbreviation"
              class="dropdown-divider"
            />
            <b-dropdown-item value="area-user" aria-role="menuitem" hasLink>
              <routerLink
                class="dropdown-item"
                role="link"
                rel="search external"
                to="/app/configuracoes-usuario"
              >
                <b-icon icon="cog-outline" class="spacing-mr-5" />
                <b> {{ General.MY_USER_AREA }} </b>
              </routerLink>
            </b-dropdown-item>
            <hr class="dropdown-divider" />
            <b-dropdown-item
              value="config-notification"
              aria-role="menuitem"
              hasLink
            >
              <routerLink
                class="dropdown-item"
                role="link"
                rel="search external"
                :to="{
                  path: '/app/configuracoes-usuario',
                  query: { activeTab: 'notifications-config' }
                }"
              >
                <b-icon icon="bell-cog-outline" class="spacing-mr-5" />
                <b> {{ General.MANAGE_NOTIFICATION }} </b>
              </routerLink>
            </b-dropdown-item>
            <hr class="dropdown-divider" />
            <b-dropdown-item value="send-feedback" aria-role="menuitem" hasLink>
              <routerLink
                class="dropdown-item"
                role="link"
                rel="search external"
                to="/app/feedback"
              >
                <b-icon icon="message-text" class="spacing-mr-5" />
                <b> {{ General.SEND_FEEDBACK }} </b>
              </routerLink>
            </b-dropdown-item>
            <hr class="dropdown-divider" />
            <b-dropdown-item value="open-site" aria-role="menuitem" hasLink>
              <routerLink
                class="dropdown-item"
                role="link"
                rel="search external"
                target="_blank"
                to="/"
              >
                <b-icon icon="home-variant-outline" class="spacing-mr-5" />
                <b> {{ General.GO_TO_SITE }} </b>
              </routerLink>
            </b-dropdown-item>
            <hr class="dropdown-divider" />
            <b-dropdown-item
              value="open-help-center"
              aria-role="menuitem"
              hasLink
            >
              <a
                class="dropdown-item"
                role="link"
                rel="noopener search external"
                href="https://suite.seplag.ce.gov.br/"
                target="_blank"
              >
                <b-icon icon="help-circle" class="spacing-mr-5" />
                <b> {{ App.Channel.HELP_CENTER }} </b>
              </a>
            </b-dropdown-item>
            <hr class="dropdown-divider" />
            <b-dropdown-item
              value="logout"
              aria-role="menuitem"
              @click="logout"
            >
              <b-icon icon="login" class="spacing-mr-5" />
              <b> {{ General.LOG_OUT }} </b>
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown
            v-if="user.bonds && user.bonds.length"
            ref="profileDropdown"
            :mobileModal="false"
            position="is-bottom-left"
            trapFocus
            class="profiles-dropdown is-hidden-touch"
          >
            <div class="label-container" @click="goBackMenu">
              <b-icon class="button spacing-mx-5" icon="chevron-left" />
              <span class="title is-6 label"> {{ General.CHANGE_SECTOR }}</span>
              <b-icon class="mx-5 is-clickable" icon="close" />
            </div>
            <hr class="dropdown-divider" />
            <b-field :type="listFilterProfile.length ? '' : 'is-danger'">
              <b-input
                v-model="filterProfile"
                :placeholder="`${General.TYPE_IT_SEARCH}...`"
                class="m-5"
              />
            </b-field>
            <b-dropdown-item
              v-for="(capacity, index) in listFilterProfile"
              :key="`${capacity.capacity_id}_${index}`"
              :value="capacity.capacity_id"
              aria-role="listitem"
              @click="selectProfile(capacity)"
            >
              <div class="spacing-pr-20 has-text-weight-semibold">
                <p>
                  {{ formatCapacity(capacity) }}
                </p>
              </div>
            </b-dropdown-item>

            <b-dropdown-item v-if="!listFilterProfile.length" disabled>
              <div class="spacing-pr-20">
                <p>
                  {{ General.MESSAGES.NO_RECORDS_FOUND }}
                </p>
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </template>

        <div
          v-if="isMobile"
          class="sidebar-menu"
          @click.stop="setSidebar(!isSidebarOpenned)"
        >
          <b-icon
            icon="menu"
            :type="isContrast ? 'is-black' : 'is-white'"
            size="is-medium"
          />
        </div>
      </section>
    </div>

    <b-collapse
      ref="collapseRef"
      v-model="isCollapseOpened"
      class="header-collapse-content"
      animation="slide"
    >
      <component
        :is="collapseComponent"
        :parentComponentRef="$refs.collapseRef"
        isCollapse
        :toggleCollapse="toggleCollapseComponent"
      ></component>
    </b-collapse>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { debounce, truncate } from "lodash";
import { App, General } from "@/utils/constants";
import NotificationBell from "@/components/NotificationBell.vue";
import NotificationBellContent from "@/components/NotificationBellContent.vue";
import MessageHeader from "@/components/MessageHeader.vue";
import MessageHeaderContent from "@/components/MessageHeaderContent.vue";
import UserHeaderMenuContent from "@/components/UserHeaderMenuContent.vue";

export default {
  name: "AppHeader",
  components: {
    MessageHeader,
    MessageHeaderContent,
    NotificationBell,
    NotificationBellContent,
    UserHeaderMenuContent
  },

  data() {
    return {
      App,
      General,
      truncate,
      searchFilter: "",
      isFetching: false,
      filterProfile: "",
      searchFocus: false,
      MessageHeaderContent,
      NotificationBellContent,
      UserHeaderMenuContent,
      collapseComponent: null,
      isCollapseOpened: false
    };
  },
  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      getSuggests: "getSuggests",
      isContrast: "isContrast",
      isSidebarOpenned: "isSidebarOpenned",
      isMobile: "isMobile"
    }),

    isBeta() {
      return process.env.VUE_APP_ENVIRONMENT === "beta";
    },
    isQA() {
      return process.env.VUE_APP_ENVIRONMENT === "qa";
    },
    getFields() {
      const list = this.getSuggests.map(suggest => ({
        name: suggest.toLowerCase()
      }));
      return this.searchFilter.length
        ? [{ name: this.searchFilter }, ...list]
        : list;
    },
    listFilterProfile() {
      const regExp = RegExp(this.filterProfile, "i");
      return this.user.bonds.filter(
        bond =>
          regExp.test(
            `${bond.part_abbreviation}/${
              bond.capacity_abbreviation.includes("/")
                ? bond.capacity_abbreviation.split("/", -1)[1]
                : bond.capacity_abbreviation
            }`
          ) && bond.active
      );
    }
  },

  watch: {
    $route() {
      this.searchFilter = "";
    },

    isMobile(value) {
      if (!value) {
        this.isCollapseOpened = false;
      }
    }
  },

  methods: {
    ...mapActions({
      getFieldsForSearch: "getFieldsForSearch",
      updateDefaultProfile: "updateDefaultProfile"
    }),
    ...mapMutations({
      setSuggests: "setSuggests",
      setViewSector: "setViewSector",
      setFilterList: "setFilterList",
      setSidebar: "setSidebar"
    }),

    formatCapacity(capacity) {
      if (!capacity) return "";
      return `${capacity.part_abbreviation}/${
        capacity.capacity_abbreviation.includes("/")
          ? capacity.capacity_abbreviation.split("/", -1)[1]
          : capacity.capacity_abbreviation
      }`.toUpperCase();
    },

    onSelectSuggestion(option) {
      if (option) {
        this.searchFilter = option.name;
        this.submitSearchProcess();
      }
    },

    setSearchField(value) {
      this.searchFilter = value;
      this.requestFields(value);
    },

    requestFields: debounce(async function(value) {
      this.isFetching = true;
      const searchTexts = value.trim().split(" ");
      this.searchFilter = value;
      searchTexts[0] !== "" &&
        (await this.getFieldsForSearch({
          filter: searchTexts[searchTexts.length - 1]
        }));
      this.isFetching = false;
    }, 600),

    onFocusSearch() {
      this.searchFocus = true;
    },

    onBlurSearch() {
      this.searchFocus = false;
    },

    goToRoot() {
      if (this.$route.path !== "/app/processos") this.$router.replace("/app");
    },

    submitSearchProcess() {
      this.setSuggests({ suggests: [] });
      this.setFilterList({
        search: this.searchFilter
      });
      this.$router.push(`/app/busca`);
    },

    logout() {
      this.$store.dispatch("authLogout", "Usuário deslogado com sucesso.");
    },

    goBackMenu() {
      this.$refs.profileDropdown.toggle();
      this.$refs.menu.toggle();
    },

    toggleProfilesDropdown() {
      this.$refs.profileDropdown.toggle();
    },

    async selectProfile(capacity) {
      this.filterProfile = "";
      const result = await this.updateDefaultProfile(capacity.capacity_id);
      if (result) {
        this.$router.push("/app");
        this.setViewSector("INDIVIDUAL");
        this.snackToast.open({
          message: `${this.General.MESSAGES.CHANGE_UNITY_TO} ${
            capacity.part_abbreviation
          }/${
            capacity.capacity_abbreviation.includes("/")
              ? capacity.capacity_abbreviation.split("/", -1)[1]
              : capacity.capacity_abbreviation
          }`,
          type: "is-success",
          duration: 8000
        });
      }
    },

    openCollapseComponent(component) {
      this.collapseComponent = component;
      this.isCollapseOpened = !this.isCollapseOpened;
    },

    toggleCollapseComponent() {
      this.isCollapseOpened = !this.isCollapseOpened;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.app-header {
  position: fixed;

  width: 100%;
  z-index: 40;

  .header-content {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 85px;
    gap: 3vw;
    border-bottom: 10px solid $green-light;
    background-color: $color-7;
  }

  .header-collapse-content {
    width: 100%;
    background-color: $color-1;
    box-shadow: 0 2px 5px $grey;
  }
  .left-section {
    display: flex;
    flex-flow: row;
    align-items: center;
    gap: 3vw;
    img {
      width: 15vw;
      min-width: 150px;
      max-width: 200px;
      @media screen and (max-width: 768px) {
        min-width: 60px;
        width: 80px;
      }
    }

    .govbrand-header {
      background-color: $color-7;
      text-align: center;
      padding-left: 35px;
      box-shadow: none;
      cursor: pointer;

      @media screen and (max-width: 1024px) and (min-width: 769px) {
        padding-left: 10px;
      }
    }
  }
  .right-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
    height: 100%;

    .env-tag {
      min-width: 200px;
      color: $blue-oil;
    }

    .menu-user {
      display: flex;
      cursor: pointer;
      margin: 0px 10px;
    }

    .profiles-dropdown {
      position: absolute;
      right: 0;
      top: 65px;

      .label-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
    }

    .button-reset-style {
      background-color: transparent;
      border-width: 0;
      font-family: inherit;
      font-size: inherit;
      font-style: inherit;
      font-weight: inherit;
      line-height: inherit;
      padding: 0;
    }

    .profile-info {
      display: flex;
      flex-direction: row;
      align-items: center;
      height: 100%;
      margin: 0 5px;
      cursor: pointer;
    }
  }
}

.user-profile {
  padding: 0 20px 0 5px;
  font-size: 0.73rem;
  color: $color-1;
}

.label {
  display: flex;
  margin: 0 20%;
}

.input[type="search"]:focus {
  border: 0;
  opacity: 1;
  outline: 0;
  width: 100%;
}

.menu-profile {
  display: flex;
  flex-direction: column;
  max-width: 95%;
}

a.dropdown-item {
  background-color: transparent;
  color: $color-7;
  outline: 0;
  width: 300px;
  display: flex;
  flex-direction: row;
  svg {
    width: 1.125em;
  }

  &:focus,
  &:hover {
    background-color: transparent;
    color: $blue-dark;

    path {
      fill: $primary;
    }
  }

  &:active {
    background-color: transparent;
    color: $blue-dark;
  }
}

.-block {
  padding: 0 20px 0 5px;
  font-size: 0.73rem;
}

@media (max-width: 1024px) {
  div.dropdown.dropdown-menu-animation.is-bottom-left
    > div.dropdown-trigger
    img {
    margin: 16px 0 5px 0;
  }

  .-block.has-text-left.has-text-white,
  .icon.has-text-white.is-small {
    display: none;
  }
}

@media (max-width: 1152px) {
  .-block.has-text-left.has-text-white {
    width: 120px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@media (max-width: 1334px) {
  .-block.has-text-left.has-text-white {
    width: 160px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.sidebar-menu {
  display: grid;
  grid-template-columns: auto 1rem 0.5rem;
  grid-template-rows: 1.5rem auto;
  .icon {
    grid-row: 1/3;
    grid-column: 1/3;
    & > i::before {
      font-size: 2.5rem !important;
    }
  }
}
:is(.message-dropdown) .dropdown-menu {
  width: 25vw !important;

  @media (max-width: $tablet) {
    min-width: 250px;
  }

  :is(.header_message) {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-right: 5px;
  }
}
</style>

<style lang="scss">
@import "@/assets/scss/variables";
.app-header {
  .left-section {
    .autocomplete .input {
      background-color: $color-1 !important;
    }
  }
}

.left-section {
  flex: 1;
  .search-input {
    flex: 1;
    .control {
      padding: 0;
      .input {
        border-radius: 20px;
      }
      ::placeholder {
        color: #787e85;
      }
      span.icon {
        color: #4a4a4a;
      }
    }
  }
}

.sidebar-user {
  .sidebar-content {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    z-index: 40;
    background-color: $background-light !important;
    min-width: 75vw !important;
    height: 90vh;
    border-radius: 10px;
  }
  &.full > .sidebar-content {
    top: 85px;
    border-radius: 0;
  }
  .user-profile-block {
    width: calc(100% - 30px);
    display: grid;
    grid-template-columns: 60px auto;
    margin-top: 5px;
    img {
      margin: 3px;
    }
  }
  .close {
    cursor: pointer;
    background-color: #dcdcdc78;
    width: 25px;
    height: 25px;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .list {
    margin-top: 10px;
    div,
    a {
      display: flex;
      padding: 10px 5px;
      border-top: 1px solid $border-color;
      width: 100%;
      font-size: 1rem;
    }
  }
  .detail {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    div {
      width: 26%;
      height: 3px;
      border-radius: 10px;
      background-color: $custom-color-4e6975;
    }
  }
  .bar-profile {
    display: flex;
    padding: 5px 10px;
    font-weight: 600;
    background-color: $custom-color-f2f2f2;
  }
  .mark-read {
    display: flex;
    justify-content: flex-end;
    margin: 10px;
    font-weight: 600;
    cursor: pointer;
  }
  .profile-item {
    font-weight: 600;
    padding: 10px 15px;
    border-bottom: 1px solid $border-color;
  }
}
</style>
